import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Plus, Trash, PencilSimple, MagnifyingGlass, BookOpenText } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";

const EMPTY = { term: "", definition: "", course_id: "", category: "" };

export default function Glossaire() {
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.role === "professeur";
  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [courseFilter, setCourseFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/glossary").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
    if (canManage) api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categories = useMemo(() => [...new Set(items.map((i) => i.category).filter(Boolean))].sort(), [items]);
  const courseOptions = useMemo(() => [...new Map(items.filter((i) => i.course_id).map((i) => [i.course_id, i.course_title])).entries()], [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (categoryFilter !== "all" && i.category !== categoryFilter) return false;
      if (courseFilter === "general" && i.course_id) return false;
      if (courseFilter !== "all" && courseFilter !== "general" && i.course_id !== courseFilter) return false;
      if (q && !(`${i.term} ${i.definition}`.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [items, search, categoryFilter, courseFilter]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((i) => {
      const letter = i.term[0]?.toUpperCase() || "#";
      (g[letter] = g[letter] || []).push(i);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const resetForm = () => { setForm(EMPTY); setEditId(null); };
  const startEdit = (i) => { setForm({ term: i.term, definition: i.definition, course_id: i.course_id || "", category: i.category || "" }); setEditId(i.id); setOpen(true); };

  const save = async () => {
    if (!form.term.trim() || !form.definition.trim()) return toast.error("Le terme et la définition sont obligatoires.");
    const payload = { term: form.term, definition: form.definition, course_id: form.course_id || null, category: form.category || null };
    try {
      if (editId) { await api.put(`/glossary/${editId}`, payload); toast.success("Terme modifié"); }
      else { await api.post("/glossary", payload); toast.success("Terme ajouté"); }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce terme du glossaire ?")) return;
    try { await api.delete(`/glossary/${id}`); toast.success("Terme supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const canEditItem = (i) => user.role === "admin" || i.created_by === user.id;

  return (
    <div className="space-y-6" data-testid="glossary-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Campus virtuel</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Glossaire</h1>
          <p className="text-sm text-muted-foreground">Lexique des termes clés — transversal ou propre à un cours.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-glossary-btn"><Plus size={18} className="mr-2" /> Nouveau terme</Button></DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId ? "Modifier le terme" : "Nouveau terme"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Terme</Label><Input value={form.term} onChange={(e) => setForm({ ...form, term: e.target.value })} data-testid="glossary-term" /></div>
                <div><Label>Définition</Label><Textarea rows={4} value={form.definition} onChange={(e) => setForm({ ...form, definition: e.target.value })} data-testid="glossary-definition" /></div>
                <div><Label>Catégorie (optionnel)</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Finance, Stratégie..." data-testid="glossary-category" /></div>
                <div>
                  <Label>Cours (optionnel)</Label>
                  <Select value={form.course_id || "__general"} onValueChange={(v) => setForm({ ...form, course_id: v === "__general" ? "" : v })}>
                    <SelectTrigger data-testid="glossary-course"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__general">Général (visible de tous)</SelectItem>
                      {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">Sans cours choisi, le terme est visible par tout le monde. Avec un cours, seuls les étudiants y ayant accès le verront.</p>
                </div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="save-glossary-btn">{editId ? "Enregistrer" : "Ajouter"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Rechercher un terme..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="glossary-search" />
        </div>
        {categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-44" data-testid="glossary-category-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes catégories</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={courseFilter} onValueChange={setCourseFilter}>
          <SelectTrigger className="w-48" data-testid="glossary-course-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les termes</SelectItem>
            <SelectItem value="general">Général uniquement</SelectItem>
            {courseOptions.map(([id, title]) => <SelectItem key={id} value={id}>{title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={BookOpenText} title="Aucun terme" description={canManage ? "Ajoutez le premier terme de ce lexique." : "Aucun terme n'a encore été ajouté."} actionLabel={canManage ? "Nouveau terme" : undefined} onAction={canManage ? () => setOpen(true) : undefined} />
      ) : (
        <div className="space-y-6">
          {grouped.map(([letter, terms]) => (
            <div key={letter}>
              <p className="mb-2 font-display text-lg font-extrabold text-primary">{letter}</p>
              <div className="space-y-2">
                {terms.map((i, idx) => (
                  <motion.div
                    key={i.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
                    data-testid={`glossary-item-${i.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-display font-bold">{i.term}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{i.definition}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {i.category && <Badge variant="secondary">{i.category}</Badge>}
                          <Badge variant="secondary">{i.course_title || "Général"}</Badge>
                        </div>
                      </div>
                      {canEditItem(i) && (
                        <div className="flex shrink-0 gap-1">
                          <Button size="icon" variant="ghost" onClick={() => startEdit(i)} data-testid={`edit-glossary-${i.id}`}><PencilSimple size={16} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => del(i.id)} data-testid={`del-glossary-${i.id}`}><Trash size={16} className="text-accent" /></Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
